import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fetchGet } from "app/utils/request";
import { UserModel } from "app/models";
import { FacilityMembership } from "app/models/UserModel";
import { Spinner } from "app/components/Loading";
import styles from "./styles.module.scss";

interface IProps {
  facilityId?: string;
  initialMembers?: UserModel[];
  loading?: boolean;
  onSave?: (_memberIds: string[]) => void;
}

const AddMembersConfig = ({ facilityId, initialMembers = [], loading, onSave }: IProps) => {
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<UserModel[]>(initialMembers);
  const [facilityCoaches, setFacilityCoaches] = useState<UserModel[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSelectedMembers(initialMembers);
  }, [initialMembers]);

  useEffect(() => {
    if (!facilityId) {
      return;
    }
    fetchGet(
      `/facilities/${facilityId}/memberships`,
      {},
      { version: 1 },
    ).then((res) => {
      const members = res as FacilityMembership[];
      const coaches = members
        .filter((m) => m.role === "coach")
        .map((m) => ({
          id: m.userId || m.id,
          displayName:
            [m.firstName, m.lastName].filter(Boolean).join(" ") || m.email,
          email: m.email,
          avatar: m.avatar,
          kind: m.isAdmin ? "coach - admin" : "coach",
        })) as UserModel[];
      setFacilityCoaches(coaches);
    });
  }, [facilityId]);

  const filteredCoaches = facilityCoaches.filter((user) => {
    const alreadySelected = selectedMembers.some((m) => m.id === user.id);
    if (alreadySelected) {
      return false;
    }

    if (!searchQuery.trim()) {
      return false;
    }

    const query = searchQuery.toLowerCase();
    const matchesName = user.displayName?.toLowerCase().includes(query);
    const matchesEmail = user.email?.toLowerCase().includes(query);
    return matchesName || matchesEmail;
  });

  const handleSelect = (user: UserModel) => {
    const updated = [...selectedMembers, user];
    setSelectedMembers(updated);
    setSearchQuery("");
    setIsDropdownOpen(false);
    inputRef.current?.focus();
    onSave?.(updated.map((m) => m.id));
  };

  const handleRemove = (userId: string) => {
    const updated = selectedMembers.filter((m) => m.id !== userId);
    setSelectedMembers(updated);
    onSave?.(updated.map((m) => m.id));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setIsDropdownOpen(e.target.value.trim().length > 0);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsDropdownOpen(false);
    }, 200);
  };

  const getRoleLabel = (user: UserModel) => {
    if (user.kindTitle) {
      return user.kindTitle;
    }
    if (user.kind) {
      return user.kind.charAt(0).toUpperCase() + user.kind.slice(1);
    }
    if (user.sport) {
      return user.sport;
    }
    return "";
  };

  const getAvatarSrc = (user: UserModel) => {
    if (typeof user.avatar === "string") {
      return user.avatar;
    }
    return "";
  };

  return (
    <div className={styles.container}>
      <p className={styles.description}>
        {t(
          "Add members who will be automatically added to the Space or Group.",
        )}
      </p>

      <div className={styles.search_wrapper}>
        <i className={styles.search_icon + " ico ico-search"} />
        <input
          ref={inputRef}
          type="text"
          className={styles.search_input}
          placeholder={t("Search by name...")}
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={() => {
            if (searchQuery.trim()) {
              setIsDropdownOpen(true);
            }
          }}
          onBlur={handleBlur}
        />

        {isDropdownOpen && filteredCoaches.length > 0 && (
          <div className={styles.dropdown}>
            {filteredCoaches.map((user) => (
              <div
                key={user.id}
                className={styles.dropdown_item}
                onMouseDown={() => handleSelect(user)}
              >
                {getAvatarSrc(user) ? (
                  <img
                    src={getAvatarSrc(user)}
                    alt=""
                    className={styles.dropdown_avatar}
                  />
                ) : (
                  <div className={styles.dropdown_avatar} />
                )}
                <div className={styles.dropdown_info}>
                  <span className={styles.dropdown_name}>
                    {user.displayName}
                  </span>
                  {getRoleLabel(user) && (
                    <span className={styles.dropdown_role}>
                      {getRoleLabel(user)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {isDropdownOpen &&
          searchQuery.trim() &&
          filteredCoaches.length === 0 && (
            <div className={styles.dropdown}>
              <div className={styles.no_results}>
                {t("No members found")}
              </div>
            </div>
          )}
      </div>

      {loading ? (
        <Spinner />
      ) : selectedMembers.length > 0 ? (
        <div className={styles.members_list}>
          {selectedMembers.map((member) => (
            <div key={member.id} className={styles.member_card}>
              {getAvatarSrc(member) ? (
                <img
                  src={getAvatarSrc(member)}
                  alt=""
                  className={styles.member_avatar}
                />
              ) : (
                <div className={styles.member_avatar} />
              )}
              <div className={styles.member_info}>
                <span className={styles.member_name}>
                  {member.displayName}
                </span>
                {getRoleLabel(member) && (
                  <span className={styles.member_role}>
                    {getRoleLabel(member)}
                  </span>
                )}
              </div>
              <button
                className={styles.remove_btn}
                onClick={() => handleRemove(member.id)}
                aria-label="remove"
              />
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.empty_state}>
          <i className={styles.empty_icon + " ico ico-user-add"} />
          <h3 className={styles.empty_title}>{t("No Added Members")}</h3>
          <p className={styles.empty_subtitle}>
            {t(
              "Members added through this workflow will appear here.",
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default AddMembersConfig;
